import clsx from 'clsx';
import { forwardRef } from 'react';

import { Info } from '../../icons';
import { textMd, textMdSemibold } from '../../typography';
import { Loader } from '../Loader';
import { Tooltip } from '../Tooltip';

import * as styles from './BaseInput.module.css';

import type { TrackingProps } from '../types';
import type { ElementType } from 'react';
import type React from 'react';

export type BaseInputProps<TElementType extends ElementType> = React.ComponentPropsWithoutRef<TElementType> &
  Partial<TrackingProps> & {
    name: string; // make it required
    label?: string;
    topHint?: string;
    tooltip?: React.ReactElement | string;
    bottomHint?: string;
    error?: string;
    before?: React.ReactElement | string;
    after?: React.ReactElement | string;
    loading?: boolean;
    showSuccess?: boolean;
    hasNoBottomContent?: boolean; // To show/hide the hint/error container below the input that creates a gap
    inputClassName?: string;
    beforeClassName?: string;
    afterClassName?: string;
  };

const BaseInputInner = <TElementType extends ElementType, TRef = React.ForwardedRef<TElementType>>(
  {
    id,
    Component,
    className,
    inputClassName,
    label,
    topHint,
    tooltip,
    before,
    after,
    bottomHint,
    error,
    loading,
    showSuccess,
    hasNoBottomContent,
    children,
    trackingId,
    trackingProps,
    beforeClassName,
    afterClassName,
    ...props
  }: BaseInputProps<TElementType> & { Component: ElementType },
  ref: TRef,
) => {
  const idWithNameFallback = id ?? props.name;
  const errorId = `${idWithNameFallback}Error`;
  const trackingIdWithNameFallback = trackingId ?? props.name;
  return (
    <div className={clsx(styles.container, className)}>
      {(label || topHint || tooltip) && (
        <div className={styles.top}>
          <label className={clsx(textMdSemibold, styles.label)} htmlFor={idWithNameFallback}>
            {label}
          </label>
          <span className={clsx(textMd, styles.topHint)}>{topHint}</span>
          {tooltip && (
            <Tooltip anchor={<Info className={styles.tooltipIcon} />} location='bottom-left' className={styles.tooltip}>
              {tooltip}
            </Tooltip>
          )}
        </div>
      )}
      <div
        className={clsx(
          styles.inputWrapper,
          error && styles.hasError,
          !error && showSuccess && styles.success,
          props.disabled && styles.disabled,
          before && styles.hasBefore,
          after && styles.hasAfter,
        )}
      >
        {before && <div className={clsx(styles.icon, styles.before, beforeClassName)}>{before}</div>}
        <div className={clsx(styles.icon, styles.loader, loading && styles.loading)}>
          <Loader loading={loading} />
        </div>
        {after && <div className={clsx(styles.icon, styles.after, afterClassName)}>{after}</div>}
        <Component
          ref={ref}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? errorId : undefined}
          className={clsx(textMd, styles.input, inputClassName)}
          objectiv={{ id: trackingIdWithNameFallback, ...trackingProps }}
          id={idWithNameFallback}
          {...props}
        >
          {children}
        </Component>
      </div>
      {!hasNoBottomContent && (
        <div className={clsx(textMd, styles.bottom)}>
          {bottomHint && !error && <span className={styles.bottomHint}>{bottomHint}</span>}
          {error && (
            <span className={styles.error} id={errorId} role='alert'>
              {error}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export const BaseInput = forwardRef(BaseInputInner);
